import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import AgentDialog from '../components/AgentDialog';
import { ImportMarkdownDialog } from '../components/ImportMarkdownDialog';
import StarterPacks from '../components/StarterPacks';
import { exportAgent } from '../lib/markdown';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Bot, MessageSquare, MoreVertical, Pencil, Trash2, Globe, Lock, Cpu, Download, FileDown } from 'lucide-react';

export default function AgentsPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [providers, setProviders] = useState({});
  const [skills, setSkills] = useState([]);
  const [dialog, setDialog] = useState({ open: false, agent: null });
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    const [a, m, s] = await Promise.all([api.get('/agents'), api.get('/models'), api.get('/skills')]);
    setAgents(a.data); setCatalog(m.data.catalog); setProviders(m.data.providers); setSkills(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startChat = async (agent) => {
    const res = await api.post('/chats', { type: 'direct', agent_ids: [agent.agent_id] });
    navigate(`/app/chats/${res.data.chat_id}`);
  };
  const del = async (agent) => { try { await api.delete(`/agents/${agent.agent_id}`); toast.success('Agent deleted'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete'); } };
  const publish = async (agent) => { const res = await api.post(`/agents/${agent.agent_id}/publish`); toast.success(res.data.is_public ? 'Published to Discover' : 'Unpublished'); load(); };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))]">
        <div><h1 className="font-display text-xl font-700">Your agents</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Build specialised AI teammates with their own brain and skills.</p></div>
        <div className="flex gap-2">
          <Button variant="secondary" className="rounded-xl gap-1.5 border border-[hsl(var(--border))]" onClick={() => setImportOpen(true)} data-testid="import-agents-button"><Download className="h-4 w-4" /> Import</Button>
          <Button className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={() => setDialog({ open: true, agent: null })} data-testid="create-agent-button"><Plus className="h-4 w-4" /> New agent</Button>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <StarterPacks onImported={() => load()} emphasize={agents.length === 0} />
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((a) => (
            <div key={a.agent_id} className="rounded-2xl border border-[hsl(var(--border))] bg-card p-4 hover:shadow-md transition-shadow flex flex-col" data-testid={`agent-card-${a.agent_id}`}>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl grid place-items-center text-xl" style={{ background: (a.color || '#0e9488') + '22' }}>{a.avatar}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5"><p className="font-600 truncate">{a.name}</p>{a.is_personal && <Badge variant="secondary" className="text-[9px]">You</Badge>}</div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mt-0.5">{a.description || 'No description'}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button className="text-[hsl(var(--muted-foreground))]" data-testid={`agent-menu-${a.agent_id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setDialog({ open: true, agent: a })}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportAgent(a)} data-testid={`export-agent-${a.agent_id}`}><FileDown className="h-4 w-4 mr-2" /> Export .md</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => publish(a)}>{a.is_public ? <><Lock className="h-4 w-4 mr-2" /> Unpublish</> : <><Globe className="h-4 w-4 mr-2" /> Publish</>}</DropdownMenuItem>
                    {!a.is_personal && <DropdownMenuItem onClick={() => del(a)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                <Badge variant="outline" className="text-[10px] gap-1 font-mono"><Cpu className="h-3 w-3" />{a.model}</Badge>
                {a.is_public && <Badge className="text-[10px] gap-1" style={{ background: 'hsl(var(--brand-mint) / 0.15)', color: 'hsl(var(--brand-mint))' }}><Globe className="h-3 w-3" />Public</Badge>}
              </div>
              <Button variant="secondary" className="rounded-xl mt-3 w-full border border-[hsl(var(--border))]" onClick={() => startChat(a)} data-testid={`chat-with-agent-${a.agent_id}`}><MessageSquare className="h-4 w-4 mr-1.5" /> Chat</Button>
            </div>
          ))}
        </div>
      </ScrollArea>
      <AgentDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} agent={dialog.agent} catalog={catalog} providers={providers} skills={skills} onSaved={() => load()} />
      <ImportMarkdownDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => load()} title="Import agents & skills" />
    </div>
  );
}
